import { useParams } from 'react-router';
import { PageBreadcrumb } from '@/components/ui/PageBreadcrumb';
import { formatUnits } from '@/components/funds-carry/funds/carryFundValues';
import { CarryEntityHeader } from '@/components/funds-carry/structure/CarryEntityHeader';
import { CarryHoldingPositionsTable } from '@/components/funds-carry/structure/CarryHoldingPositionsTable';
import { PORTAL_CARRY_STRUCTURE_PATH } from '../carryPaths';
import { memberViaLabel } from '../components/structure/memberStructure';
import { PortalStructureData } from '../components/structure/PortalStructureData';
import type { CarryHolding, CarryStructure } from '../services/carryService';

function FundDetail({
  fundId,
  structure,
  positions,
}: {
  fundId: string;
  structure: CarryStructure;
  positions: CarryHolding[];
}) {
  const fund = structure.funds.find((f) => f.fund_id === fundId);
  if (!fund) {
    return (
      <p className="text-sm text-muted-foreground">
        You hold no carry in this fund.
      </p>
    );
  }

  const name = fund.display_name ?? fund.name;
  const position = positions.filter((p) => p.fund_id === fundId);
  const units = position.reduce((sum, p) => sum + p.units, 0);
  return (
    <div className="space-y-6">
      <PageBreadcrumb
        segments={[
          { label: 'Structure', href: PORTAL_CARRY_STRUCTURE_PATH },
          { label: name },
        ]}
      />
      <CarryEntityHeader
        kind="Fund"
        title={name}
        description={`Statutory director and AFM-registered manager: ${structure.board}.`}
        facts={[
          { label: 'CoC', value: fund.coc_number ?? '—' },
          { label: 'Board', value: structure.board },
          { label: 'Units', value: `${formatUnits(units)} of your units` },
        ]}
      />
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Your position in this fund</h3>
          <p className="text-sm text-muted-foreground">
            You only see your own position
          </p>
        </div>
        <CarryHoldingPositionsTable
          positions={position}
          rowLabel={() => ({
            name: structure.full_name,
            sub: memberViaLabel(structure),
          })}
        />
      </div>
    </div>
  );
}

export function PortalCarryStructureFundPage() {
  const { fundId } = useParams<{ fundId: string }>();

  return (
    <PortalStructureData>
      {(structure, positions) => (
        <FundDetail
          fundId={fundId ?? ''}
          structure={structure}
          positions={positions}
        />
      )}
    </PortalStructureData>
  );
}
